import { ArrowUpRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ATHENA } from "@/constants/testIds";

const COLUMNS = [
  {
    title: "Company",
    links: [
      { label: "About", target: "top" },
      { label: "Mission", target: "about" },
      { label: "Careers", href: "/careers" },
      { label: "Press", target: null },
    ],
  },
  {
    title: "Domains",
    links: [
      { label: "Nutraceuticals", target: "domains" },
      { label: "Biotechnology", target: "domains" },
      { label: "Life Sciences", target: "domains" },
      { label: "R&D Services", target: "science" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Publications", target: null },
      { label: "Quality & Compliance", target: null },
      { label: "Sustainability", target: null },
      { label: "Newsletter", target: null },
    ],
  },
];

export const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const goTo = (target) => (e) => {
    e.preventDefault();
    // If not on home, navigate there first with target in state
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: target } });
      return;
    }
    if (target === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(target);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <footer
      data-testid={ATHENA.footer}
      className="relative bg-[#0A1713] text-[#F8F7F4] border-t border-[#1A382A]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-12">
          <div className="lg:col-span-5">
            <span className="label-mono">/ ATHENA NUTRASCIENCE</span>
            <p className="font-display text-2xl md:text-3xl mt-6 leading-snug max-w-md">
              Bridging the wisdom of nature with the intelligence of science.
            </p>
            <div className="mt-8 md:mt-10 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full border border-[#B89B64] flex items-center justify-center shrink-0">
                <span className="w-2 h-2 rounded-full bg-[#B89B64]" />
              </span>
              <span className="label-mono text-[#B89B64]">ISO 9001 · ISO 22000 · HALAL · KOSHER</span>
            </div>
          </div>

          {/* Link groups — 4 columns on mobile, keep desktop bento */}
          <div className="lg:col-span-7 grid grid-cols-4 gap-4 md:gap-6 lg:gap-8">
            {COLUMNS.map((c) => (
              <div key={c.title}>
                <div className="label-mono text-[10px] md:text-xs">/ {c.title}</div>
                <ul className="mt-4 md:mt-6 space-y-2 md:space-y-3">
                  {c.links.map((l) => {
                    const className =
                      "text-xs md:text-sm text-[#A2B3AB] hover:text-[#F8F7F4] flex items-center gap-1 group";
                    if (l.href) {
                      return (
                        <li key={l.label}>
                          <Link to={l.href} className={className}>
                            <span className="truncate">{l.label}</span>
                            <ArrowUpRight
                              size={12}
                              strokeWidth={1.5}
                              className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                            />
                          </Link>
                        </li>
                      );
                    }
                    return (
                      <li key={l.label}>
                        <a
                          href="#"
                          onClick={l.target ? goTo(l.target) : (e) => e.preventDefault()}
                          className={className}
                        >
                          <span className="truncate">{l.label}</span>
                          <ArrowUpRight
                            size={12}
                            strokeWidth={1.5}
                            className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            <div>
              <div className="label-mono text-[10px] md:text-xs">/ FOLLOW</div>
              <ul className="mt-4 md:mt-6 space-y-2 md:space-y-3 text-xs md:text-sm text-[#A2B3AB]">
                <li><a href="#" className="link-underline">LinkedIn</a></li>
                <li><a href="#" className="link-underline">X / Twitter</a></li>
                <li><a href="#" className="link-underline">Research Gate</a></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Massive wordmark */}
        <div className="mt-12 md:mt-14 -mb-6 select-none">
          <div className="font-display leading-none tracking-tighter text-[18vw] md:text-[16vw] text-[#1A382A]">
            athena.
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-[#1A382A] flex flex-col md:flex-row justify-between gap-4 text-xs text-[#A2B3AB]">
          <div data-testid={ATHENA.footerCopyright}>
            © {new Date().getFullYear()} Athena Nutrascience Pvt. Ltd. · All rights reserved.
          </div>
          <div className="flex items-center gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="link-underline">Privacy</Link>
            <a href="/sitemap.xml" data-testid="footer-sitemap-link" target="_blank" rel="noopener noreferrer" className="link-underline">Sitemap</a>
            <Link to="/terms" data-testid="footer-terms-link" className="link-underline">Terms</Link>
            <Link to="/quality" data-testid="footer-quality-link" className="link-underline">Quality Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
